import { Command } from './commandManager';
import { Task, FullProject } from '../types';

export interface DeleteTaskCommandOptions {
  task: Task;
  subtasks?: Task[];
  projectId: string;
  onRestore: (task: Task, subtasks?: Task[], projectId?: string) => void | Promise<void>;
  onDelete: (taskId: string | number, projectId?: string) => void | Promise<void>;
}

export function createDeleteTaskCommand({
  task,
  subtasks = [],
  projectId,
  onRestore,
  onDelete
}: DeleteTaskCommandOptions): Command {
  const taskCopy: Task = JSON.parse(JSON.stringify(task));
  const subtasksCopy: Task[] = JSON.parse(JSON.stringify(subtasks));

  return {
    id: `delete-task-${task.id}-${Date.now()}`,
    name: `Aufgabe „${task.name || 'Ohne Titel'}“ gelöscht`,
    timestamp: Date.now(),
    execute: async () => {
      await onDelete(task.id, projectId);
    },
    undo: async () => {
      await onRestore(taskCopy, subtasksCopy, projectId);
    },
    redo: async () => {
      await onDelete(task.id, projectId);
    }
  };
}

export interface MoveTaskCommandOptions {
  taskId: string | number;
  taskName: string;
  sourceProjectId: string;
  targetProjectId: string;
  previousValues: {
    start?: string;
    end?: string;
    status?: string;
    sortOrder?: number;
    parentId?: string | number;
    projectId?: string;
  };
  newValues: {
    start?: string;
    end?: string;
    status?: string;
    sortOrder?: number;
    parentId?: string | number;
    projectId?: string;
  };
  onApply: (values: MoveTaskCommandOptions['previousValues'], targetProjectId: string) => void | Promise<void>;
}

export function createMoveTaskCommand({
  taskId,
  taskName,
  sourceProjectId,
  targetProjectId,
  previousValues,
  newValues,
  onApply
}: MoveTaskCommandOptions): Command {
  return {
    id: `move-task-${taskId}-${Date.now()}`,
    name: `Aufgabe „${taskName}“ verschoben`,
    timestamp: Date.now(),
    execute: async () => {
      await onApply(newValues, targetProjectId);
    },
    undo: async () => {
      await onApply(previousValues, sourceProjectId);
    },
    redo: async () => {
      await onApply(newValues, targetProjectId);
    }
  };
}

export interface UpdateTaskStatusCommandOptions {
  taskId: string | number;
  taskName: string;
  projectId: string;
  previousStatus?: string;
  previousCompleted?: boolean;
  newStatus?: string;
  newCompleted?: boolean;
  onApply: (status?: string, completed?: boolean, projectId?: string) => void | Promise<void>;
}

export function createUpdateTaskStatusCommand({
  taskId,
  taskName,
  projectId,
  previousStatus,
  previousCompleted,
  newStatus,
  newCompleted,
  onApply
}: UpdateTaskStatusCommandOptions): Command {
  const isCompletedNow = !!newCompleted || newStatus === 'completed';
  const name = isCompletedNow
    ? `Aufgabe „${taskName}“ erledigt`
    : `Status von „${taskName}“ geändert`;

  return {
    id: `status-task-${taskId}-${Date.now()}`,
    name,
    timestamp: Date.now(),
    execute: async () => {
      await onApply(newStatus, newCompleted, projectId);
    },
    undo: async () => {
      await onApply(previousStatus, previousCompleted, projectId);
    },
    redo: async () => {
      await onApply(newStatus, newCompleted, projectId);
    }
  };
}

export interface GenericSnapshotCommandOptions {
  name: string;
  previousSnapshot: FullProject[];
  newSnapshot: FullProject[];
  onApplySnapshot: (snapshot: FullProject[]) => void | Promise<void>;
}

export function createGenericSnapshotCommand({
  name,
  previousSnapshot,
  newSnapshot,
  onApplySnapshot
}: GenericSnapshotCommandOptions): Command {
  const prevCopy: FullProject[] = JSON.parse(JSON.stringify(previousSnapshot));
  const newCopy: FullProject[] = JSON.parse(JSON.stringify(newSnapshot));

  return {
    id: `snapshot-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
    name,
    timestamp: Date.now(),
    execute: async () => {
      await onApplySnapshot(newCopy);
    },
    undo: async () => {
      await onApplySnapshot(prevCopy);
    },
    redo: async () => {
      await onApplySnapshot(newCopy);
    }
  };
}
